import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutGrid,
  GraduationCap,
  TrendingUp,
  Timer,
  User,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Compass,
  Calendar,
  Bookmark,
  Bell,
  Crown,
  Users,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Logo } from './Logo';
import { ReviewPrompt } from './ReviewPrompt';

const nav = [
  { to: '/app', label: 'Discover', icon: Compass, end: true },
  { to: '/app/courses', label: 'Course', icon: GraduationCap },
  { to: '/app/mentorship', label: 'Mentorship', icon: Crown },
  { to: '/app/tools', label: 'Tools', icon: Timer },
  { to: '/app/community', label: 'Community', icon: Users },
  { to: '/app/schedule', label: 'Schedule', icon: Calendar },
  { to: '/app/profile', label: 'Profile', icon: User },
];

function NavItem({ item, onClick, badge }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-white/10 text-white shadow-inner_glow'
            : 'text-zinc-400 hover:bg-white/5 hover:text-white'
        )
      }
    >
      <Icon size={18} className="transition-transform group-hover:scale-110" />
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-emerald px-1.5 text-[11px] font-bold text-black tnum" data-testid="nav-unread-badge">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = () =>
      api
        .get('/notifications')
        .then(({ data }) => alive && setUnread(data.unread || 0))
        .catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const items = [...nav];
  if (user?.is_admin) items.push({ to: '/app/admin', label: 'Admin', icon: ShieldCheck });

  const doLogout = async () => {
    await logout();
    navigate('/');
  };

  const Sidebar = ({ onNav }) => (
    <div className="flex h-full flex-col">
      <Link to="/app" onClick={onNav} className="flex items-center gap-2 px-2 py-1" data-testid="sidebar-logo">
        <Logo size={36} />
      </Link>

      <nav className="mt-8 flex flex-1 flex-col gap-1">
        {items.map((it) => (
          <NavItem key={it.to} item={it} onClick={onNav} badge={0} />
        ))}
      </nav>

      <div className="mt-4 rounded-xl border border-white/5 bg-ink-surface p-3">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-ink-elevated text-sm text-white">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              (user?.name || 'U').charAt(0).toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-white">{user?.name}</div>
            <div className="truncate text-[11px] text-zinc-500">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 py-2 text-xs text-zinc-400 transition-colors hover:border-red-500/40 hover:text-red-400"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-base">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-white/5 bg-ink-surface/60 p-4 backdrop-blur-xl lg:block">
        <Sidebar />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-black/60 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Link to="/app" className="flex items-center gap-2">
          <Logo size={32} />
        </Link>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="text-zinc-300">
          <Menu />
        </button>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/70 lg:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed inset-y-0 left-0 z-50 w-72 border-r border-white/10 bg-ink-surface p-4 lg:hidden"
            >
              <button onClick={() => setOpen(false)} className="absolute right-4 top-4 text-zinc-400">
                <X />
              </button>
              <Sidebar onNav={() => setOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] px-5 py-8 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>

      <ReviewPrompt />
    </div>
  );
}
